import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

const inputVariants = cva(
  cn(
    'flex h-12 w-full text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-within:border-primary-foreground focus-within:ring-1 focus-within:ring-primary',
    'border-box items-center gap-2',
  ),
  {
    variants: {
      variant: {
        outlined: ['rounded-md bg-background border-input border', 'ea-input__outlined'],
        filled: ['rounded-md bg-input', 'ea-input__filled'],
      },

      rounded: {
        default: ['rounded-lg', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },

    defaultVariants: {
      rounded: 'default',
      variant: 'outlined',
    },
  },
);
export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof inputVariants> {
  before?: React.ReactNode;
  after?: React.ReactNode;

  beforeOuter?: React.ReactNode;
  afterOuter?: React.ReactNode;

  className?: {
    wrapper?: string;
    main?: string;
    before?: string;
    after?: string;
  };
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, before, after, afterOuter, beforeOuter, ...props }, ref) => {
    return (
      <>
        {beforeOuter}
        <div
          className={cn(
            inputVariants({
              rounded: props.rounded,
              className: cn(
                'ea-input__wrapper',
                props['aria-invalid'] &&
                  'ea-input__wrapper--invalid border-destructive text-destructive',
                !!before && 'pl-3',
                !!after && 'pr-3',
                className?.wrapper,
              ),
            }),
          )}
        >
          {before}
          <input
            type={type}
            className={cn(
              'h-full w-full flex-1 rounded-lg bg-transparent  px-3 py-1 hover:outline-none focus:outline-none',
              'file:h-full file:cursor-pointer file:rounded-md file:border-none file:border-input file:bg-secondary file:outline-none file:hover:bg-secondary/90',
              !!before && 'pl-0',
              !!after && 'pr-0',

              'ea-input__main',
              className?.main,
            )}
            ref={ref}
            {...props}
          />
          {after}
        </div>
        {afterOuter}
      </>
    );
  },
);
Input.displayName = 'Input';

export { Input };
